'use client';

import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useAgentContext } from '../../context/AgentContext';
import { listConversations, getConversation, deleteConversation } from '../../services/agentApi';
import ConversationListItem from './ConversationListItem';
import NewChatButton from './NewChatButton';

export default function ConversationList() {
  const { state, setConversations, setActiveConversation, setMessages, toggleHistory } =
    useAgentContext();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        const data = await listConversations();
        setConversations(data.conversations);
      } catch {
        console.error('Failed to load conversations');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [setConversations]);

  const handleSelect = async (conversationId: string) => {
    try {
      const data = await getConversation(conversationId);
      setActiveConversation(conversationId);
      setMessages(data.messages);
      toggleHistory();
    } catch {
      console.error('Failed to load conversation');
    }
  };

  const handleDelete = async (conversationId: string) => {
    try {
      await deleteConversation(conversationId);
      setConversations(state.conversations.filter(c => c.conversationId !== conversationId));
      if (state.activeConversationId === conversationId) {
        setMessages([]);
      }
    } catch {
      console.error('Failed to delete conversation');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} sx={{ color: 'var(--primary-color)' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: '1rem' }}>
      <NewChatButton />

      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: 'var(--sw-white-color-90)',
          opacity: 0.6,
          mt: 2,
          mb: 1,
          px: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontSize: '0.7rem',
        }}
      >
        Recent conversations
      </Typography>

      {state.conversations.length === 0 ? (
        <Typography
          variant="body2"
          sx={{ color: 'var(--sw-white-color-90)', textAlign: 'center', py: 2, opacity: 0.6 }}
        >
          No conversations yet
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {state.conversations.map(conv => (
            <ConversationListItem
              key={conv.conversationId}
              conversation={conv}
              isActive={conv.conversationId === state.activeConversationId}
              onSelect={handleSelect}
              onDelete={handleDelete}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
